"use client";

import Link from "next/link";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import HeroCrossfade from "@/components/dashboard/jtbd/HeroCrossfade";
import BeforeAfterSlider from "./BeforeAfterSlider";
import {
  BADGE_CLASS,
  EDIT_CHAT_IMAGE,
  EDIT_CHAT_SUGGESTIONS,
  getHeroProducts,
  SELFIE_AFTER_HEADSHOT,
  SELFIE_BEFORE,
  type HeroProduct,
} from "./heroLabData";

const SLIDE_MS = 7000;

type Slide = {
  product: HeroProduct;
  fill: string;
  headline: string;
  subhead: string;
  cta: string;
  media: ReactNode;
};

function TypingPrompt() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setI((n) => (n + 1) % EDIT_CHAT_SUGGESTIONS.length), 1800);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="absolute bottom-4 left-1/2 w-[86%] -translate-x-1/2 rounded-2xl bg-white/95 p-2.5 shadow-lg backdrop-blur">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#5956E9] text-xs text-white">✨</span>
        <span key={i} className="flex-1 truncate text-sm font-medium text-gray-800 animate-[fadeIn_400ms_ease-out]">
          {EDIT_CHAT_SUGGESTIONS[i]}
        </span>
        <span className="rounded-full bg-[#5956E9] px-2.5 py-1 text-[11px] font-bold text-white">Send</span>
      </div>
    </div>
  );
}

/** Option D - the big banner becomes a 3-slide spotlight with tabs (replaces the Headshots banner). */
export default function HeroSpotlight() {
  const products = useMemo(() => getHeroProducts(), []);
  const slides: Slide[] = useMemo(
    () => [
      {
        product: products.headshots,
        fill: "#173A6E",
        headline: "Professional headshots, studio-quality.",
        subhead: "515+ outfits, backgrounds, and poses - from one selfie.",
        cta: "Create headshots",
        media: (
          <BeforeAfterSlider
            before={SELFIE_BEFORE}
            after={SELFIE_AFTER_HEADSHOT}
            beforeLabel="Your selfie"
            afterLabel="AI headshot"
            className="h-full w-full"
          />
        ),
      },
      {
        product: products["edit-chat"],
        fill: "#5956E9",
        headline: "Just say what to change.",
        subhead:
          "Swap outfits, fix hair, add a smile - and clean up the plastic skin and warped details left by other AI edits.",
        cta: "Edit with chat",
        media: (
          <>
            <img src={EDIT_CHAT_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover" />
            <TypingPrompt />
          </>
        ),
      },
      {
        product: products.photoshoot,
        fill: "#C4785A",
        headline: "Lifestyle photos for dating, work, and everyday.",
        subhead: "Now on GPT Image 2.5 Flare - sharper results, faster.",
        cta: "Start photoshoot",
        media: (
          <HeroCrossfade
            images={products.photoshoot.images}
            imageClassName="inset-0 h-full w-full object-cover object-[center_22%]"
          />
        ),
      },
    ],
    [products]
  );

  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const id = window.setTimeout(() => setIndex((n) => (n + 1) % slides.length), SLIDE_MS);
    return () => window.clearTimeout(id);
  }, [index, paused, slides.length]);

  const slide = slides[index];

  return (
    <section className="mb-8 md:mb-10" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      <div
        className="relative overflow-hidden rounded-2xl transition-colors duration-700 sm:rounded-3xl"
        style={{ backgroundColor: slide.fill }}
      >
        <div className="grid min-h-[320px] sm:grid-cols-[1.25fr_1fr] sm:min-h-[340px]">
          <div className="relative z-10 flex flex-col justify-center px-5 py-7 sm:px-8 lg:px-10">
            <span className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
              {slide.product.label}
              {slide.product.badge ? (
                <span className={`rounded-full px-1.5 py-0.5 text-[9px] tracking-normal ${BADGE_CLASS[slide.product.badge]}`}>
                  {slide.product.badge}
                </span>
              ) : null}
            </span>
            <h1
              key={slide.headline}
              className="mt-2 text-[1.6rem] font-bold leading-[1.12] text-white sm:text-[2rem] lg:text-[2.5rem]"
              style={{ fontFamily: "var(--font-merriweather)" }}
            >
              {slide.headline}
            </h1>
            <p className="mt-2 max-w-md text-sm leading-relaxed text-white/80 sm:text-[15px]">{slide.subhead}</p>
            <Link
              href={slide.product.href}
              className="mt-5 inline-flex w-fit rounded-full bg-white px-6 py-2.5 text-sm font-bold shadow-sm transition hover:brightness-95"
              style={{ color: slide.fill }}
            >
              {slide.cta} →
            </Link>
          </div>
          <div className="relative h-[260px] sm:h-auto">
            {slides.map((s, i) => (
              <div
                key={s.product.id}
                className="absolute inset-0 transition-opacity duration-700"
                style={{ opacity: i === index ? 1 : 0, pointerEvents: i === index ? "auto" : "none" }}
              >
                {s.media}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2 sm:gap-3">
        {slides.map((s, i) => (
          <button
            key={s.product.id}
            type="button"
            onClick={() => setIndex(i)}
            className={`group relative overflow-hidden rounded-xl border px-3 py-2.5 text-left transition ${
              i === index ? "border-gray-300 bg-white shadow-sm" : "border-transparent bg-gray-50 hover:bg-white"
            }`}
          >
            <span className="flex items-center gap-2">
              <span className="text-base" aria-hidden>
                {s.product.icon}
              </span>
              <span className="truncate text-sm font-bold text-gray-900">{s.product.label}</span>
            </span>
            <span className="mt-0.5 hidden truncate text-xs text-gray-500 sm:block">{s.product.blurb}</span>
            <span className="absolute inset-x-0 bottom-0 h-0.5 bg-gray-100">
              <span
                key={`${index}-${paused}`}
                className="block h-full"
                style={{
                  backgroundColor: s.fill,
                  width: i < index ? "100%" : "0%",
                  animation:
                    i === index && !paused ? `heroLabProgress ${SLIDE_MS}ms linear forwards` : undefined,
                }}
              />
            </span>
          </button>
        ))}
      </div>
      <style>{`@keyframes heroLabProgress{from{width:0%}to{width:100%}}@keyframes fadeIn{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:none}}`}</style>
    </section>
  );
}
